/**
 * Admin Billing Management Page
 * Shows user entitlements and lets admins grant/revoke plans or top-up credits.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import type { User } from './users';
import { EntitlementDialog } from '../../components/admin/EntitlementDialog';
import { planDisplay } from '../../config/pricing';

export const Route = createFileRoute('/admin/billing')({
  component: AdminBilling,
});

type Plan = 'NONE' | 'UNLIMITED';

export interface AdminUserEntitlementSummary {
  userId: string;
  email: string;
  displayName: string | null;
  plan: Plan;
  source: string;
  videosRemaining: number | null;
  videosUsedPeriod: number;
  videosPerPeriod: number | null;
  periodEnd: string | null;
  grantExpiresAt: string | null;
}

function AdminBilling() {
  const [users, setUsers] = useState<User[]>([]);
  const [entitlements, setEntitlements] = useState<Record<string, AdminUserEntitlementSummary>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  async function loadAll() {
    setLoading(true);
    try {
      const userResp = await api.get('/approval/users');
      const list: User[] = userResp.data;
      setUsers(list);

      // Fetch each user's entitlement. Small N (<200) — acceptable for launch.
      const results = await Promise.all(
        list.map(async (u) => {
          try {
            const r = await api.get(`/admin/users/${u.id}/entitlement`);
            return [u.id, {
              userId: u.id,
              email: u.email,
              displayName: u.displayName,
              plan: r.data.entitlement.plan,
              source: r.data.entitlement.source,
              videosRemaining: r.data.entitlement.videosRemaining,
              videosUsedPeriod: r.data.entitlement.videosUsedPeriod,
              videosPerPeriod: r.data.entitlement.videosPerPeriod,
              periodEnd: r.data.entitlement.periodEnd,
              grantExpiresAt: r.data.entitlement.grantExpiresAt,
            } as AdminUserEntitlementSummary] as const;
          } catch {
            return null;
          }
        })
      );
      const map: Record<string, AdminUserEntitlementSummary> = {};
      for (const r of results) {
        if (r) map[r[0]] = r[1];
      }
      setEntitlements(map);
    } catch (err) {
      console.error('Failed to load billing data:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => !term || u.email.toLowerCase().includes(term) || (u.displayName ?? '').toLowerCase().includes(term))
      .map((u) => entitlements[u.id])
      .filter(Boolean);
  }, [users, entitlements, search]);

  const selected = selectedUserId ? entitlements[selectedUserId] : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Billing</h1>
          <p className="text-muted-foreground">Manage user plans, grants, and credit top-ups.</p>
        </div>
        <Button variant="outline" onClick={() => loadAll()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All users</CardTitle>
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Filter by email or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Period ends</TableHead>
                  <TableHead>Grant expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => {
                  // Same single-source-of-truth display the user's own billing page
                  // uses — admin and user can never disagree (the old bug).
                  const d = planDisplay(e);
                  return (
                  <TableRow key={e.userId}>
                    <TableCell>
                      <div className="font-medium">{e.displayName || '—'}</div>
                      <div className="text-xs text-muted-foreground">{e.email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={d.tone === 'free' ? 'secondary' : 'default'}>{d.label}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.source}</TableCell>
                    <TableCell className="text-sm">
                      <span>{e.videosRemaining ?? 0} remaining</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {e.periodEnd ? new Date(e.periodEnd).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {e.grantExpiresAt ? new Date(e.grantExpiresAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setSelectedUserId(e.userId)}>
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No matching users.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selected && (
        <EntitlementDialog
          user={selected}
          onClose={() => setSelectedUserId(null)}
          onChanged={async () => {
            await loadAll();
          }}
        />
      )}
    </div>
  );
}
